import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { selectAppName, selectAppSettings } from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import Select from 'antd/lib/select';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import { PossibleDefaultReleaseTypes } from '../../types';
import { displayTextForReleaseType } from '../../helpers/displayHelpers';
import { globalMessage } from '../../utils/globalMessage';
import Switch from 'antd/lib/switch';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface EditAppSettingsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditAppSettingsModal: React.FC<EditAppSettingsModalProps> = (props: EditAppSettingsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const editAppSettingsIsLoading = useIsLoading(AppSettingsUpdatable.UpdateVersioningDefaults);
    const editAppSettingsError = useError(AppSettingsUpdatable.UpdateVersioningDefaults);
    const editAppSettingsSuccess = useUpdated(AppSettingsUpdatable.UpdateVersioningDefaults);

    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const appPlatform = useSelector((state: RootState) => selectAppSettings(state, props.appId).platform);

    const defaultReleaseType = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).defaultReleaseType,
    );
    const assumePointReleasesAreHotfixes = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).assumePointReleasesAreHotfixes,
    );

    const editAppSettings = (values) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateVersioningDefaults, {
                orgId: props.orgId,
                appId: props.appId,
                defaultReleaseType: values.defaultReleaseType,
                assumePointReleasesAreHotfixes: values.assumePointReleasesAreHotfixes,
            }),
        );
    };

    React.useEffect(() => {
        if (editAppSettingsError) {
            globalMessage.error(editAppSettingsError);
        }
    }, [editAppSettingsError]);

    React.useEffect(() => {
        if (editAppSettingsSuccess) {
            globalMessage.success('Versioning defaults updated!');

            props.onCancel();
        }
    }, [editAppSettingsSuccess]);

    React.useEffect(() => {
        form.setFieldsValue({
            defaultReleaseType: defaultReleaseType,
            assumePointReleasesAreHotfixes: assumePointReleasesAreHotfixes,
        });
    }, []);

    return (
        <Modal
            title="Edit version settings"
            okButtonProps={{ loading: editAppSettingsIsLoading }}
            onOk={() => form.submit()}
            okText="Save"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                form.resetFields();
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text>
                    These settings will be used by Runway when automatically creating new release versions for this app.
                </Text>
                <Form
                    onFinish={editAppSettings}
                    initialValues={{
                        defaultReleaseType: defaultReleaseType,
                        assumePointReleasesAreHotfixes: assumePointReleasesAreHotfixes,
                    }}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        help={`The default release type used by Runway when automatically creating new release versions for ${appName} ${appPlatform}.`}
                        name="defaultReleaseType"
                        label="Default release type"
                        rules={[
                            {
                                required: true,
                                message: 'Choose a default release type.',
                            },
                        ]}
                        style={{ width: 300 }}
                        labelAlign={'left'}
                    >
                        <Select placeholder={'Select'} autoFocus>
                            {PossibleDefaultReleaseTypes.map((releaseType, index) => {
                                return (
                                    <Select.Option key={index} value={releaseType}>
                                        {displayTextForReleaseType(releaseType)}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="assumePointReleasesAreHotfixes"
                        label="Assume point releases are hotfixes"
                        valuePropName="checked"
                        style={{ paddingTop: 12 }}
                    >
                        <Switch disabled={false} />
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default EditAppSettingsModal;
